import React, { useEffect, useState } from 'react';
import { useTranslations } from 'hooks/useTranslations';
import { Box, Grid } from '@mui/material';
import { FormModal } from 'components/molecules/Modal';
import { FormSelect } from 'components/molecules/FormSelect';
import { Resolver, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Elabels } from 'pages/CaseManagementPage/CasesPage/helpers';
import { useCase } from '../../hooks';
import { useDictionary } from 'models/dictionaryContext';
import { useLang } from 'models/langContext';
import { mapDictionaryToOptionProp } from 'helpers/dictionary';
import { Alert, EAlertVariants } from 'components/atoms/Alert';
import { ActionTypes } from 'state/actions/alert';
import { useAlert } from 'models/alertContext';

interface IFormValues {
  paymentStatusUniqueId: string;
}

interface ICaseApprovalForm {
  caseId: string;
  paymentId: string | undefined;
  close: () => void;
  isOpen: boolean;
}

const CaseUpdatePaymentForm = ({ caseId, paymentId, close, isOpen }: ICaseApprovalForm) => {
  const { t } = useTranslations();
  const { handleUpdatePayment } = useCase();
  const { selectedLanguage } = useLang();
  const [isSubmitLoading, setSubmitLoading] = useState(false);
  const { setAlert, clearAlert, modalAlert } = useAlert();
  const { weaponLicencePaymentStatus, getWeaponLicencePaymentStatus } = useDictionary();

  useEffect(() => {
    if (!weaponLicencePaymentStatus) {
      getWeaponLicencePaymentStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  const initialFormValues = { paymentStatusUniqueId: paymentId };

  const schema = yup.object({
    paymentStatusUniqueId: yup.string().required(t('general.field.validation.required')),
  });
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm<IFormValues>({
    defaultValues: initialFormValues,
    resolver: yupResolver(schema) as unknown as Resolver<IFormValues>,
  });

  useEffect(() => {
    if (paymentId) {
      reset({ paymentStatusUniqueId: paymentId });
    }
  }, [paymentId]);

  useEffect(() => {
    clearAlert(ActionTypes.CLEAR_MODAL_ALERT);
  }, [isOpen]);

  const onsubmit = async (data: IFormValues) => {
    if (isSubmitLoading) return false;
    if (data.paymentStatusUniqueId === paymentId) {
      setAlert(ActionTypes.SET_MODAL_ALERT, {
        text: t('general.noChanged.warning.message'),
        variant: EAlertVariants.warning,
      });
    } else {
      setSubmitLoading(true);
      await handleUpdatePayment(caseId, data.paymentStatusUniqueId);
      close();
      clearAlert(ActionTypes.CLEAR_MODAL_ALERT);
      setSubmitLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onsubmit)}>
      <FormModal
        open={isOpen}
        onClickClose={() => {
          close();
          clearAlert(ActionTypes.CLEAR_MODAL_ALERT);
          reset(initialFormValues);
        }}
        onClickApprove={handleSubmit(onsubmit)}
        title={t(Elabels.updatePayment)}
        titleLeft
      >
        <Grid sx={{ gap: 1, mb: 1, mt: 1 }}>
          <Grid size={{ xs: 12 }}>
            <FormSelect
              options={mapDictionaryToOptionProp(weaponLicencePaymentStatus)}
              name="paymentStatusUniqueId"
              setValue={setValue}
              label={t(Elabels.paymentStatus)}
              control={control}
              errors={errors}
              id="update_payment_payment_status_unique_id-select"
            />
          </Grid>
          {modalAlert && (
            <Grid size={{ xs: 12 }}>
              <Box>
                <Alert text={modalAlert.text} variant={modalAlert.variant} />
              </Box>
            </Grid>
          )}
        </Grid>
      </FormModal>
    </form>
  );
};

export default CaseUpdatePaymentForm;
